import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Trophy, Zap, Target, Award, Clock, Users, ChevronRight,
  Star, Swords, Sun, Moon, BookOpen, TrendingUp, Shield,
  CheckCircle, XCircle, Medal, Brain, Flame,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// ── Dark mode hook ────────────────────────────────────────────────────────────
function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("tg-theme") === "dark"; } catch { return false; }
  });
  useEffect(() => {
    localStorage.setItem("tg-theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

// ── Animated counter ──────────────────────────────────────────────────────────
function Counter({ target, suffix = "" }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = Math.ceil(target / 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setVal(target); clearInterval(timer); }
      else setVal(start);
    }, 24);
    return () => clearInterval(timer);
  }, [target]);
  return <span>{val.toLocaleString()}{suffix}</span>;
}

// ── Comparison row ────────────────────────────────────────────────────────────
const comparisons = [
  { topic: "Sections / Clauses", old: "911 Sections", new_: "536 Clauses" },
  { topic: "Word Count", old: "5.12 Lakh Words", new_: "2.56 Lakh Words" },
  { topic: "Tax Year Concept", old: "Previous Year + Assessment Year", new_: "Single 'Tax Year'" },
  { topic: "Effective From", old: "April 1, 1962", new_: "April 1, 2026" },
  { topic: "Default Regime", old: "Old Tax Regime", new_: "New Tax Regime" },
  { topic: "Provisos", old: "~1,200 Provisos", new_: "Largely Removed" },
];

// ── GP scoring card ───────────────────────────────────────────────────────────
const scoring = [
  { icon: "📚", label: "Easy Question (Basic)", gp: "+8 GP" },
  { icon: "🎯", label: "Medium Question (Moderate)", gp: "+10 GP" },
  { icon: "🔥", label: "Hard Question (Advanced)", gp: "+15 GP" },
  { icon: "👑", label: "Expert Question", gp: "+20 GP", highlight: true },
  { icon: "⚡", label: "Speed Bonus (< 2 min)", gp: "+30 GP" },
  { icon: "🔥", label: "Streak Bonus (3+ correct)", gp: "+2 GP each" },
  { icon: "🏆", label: "Challenge Win", gp: "+150 GP" },
  { icon: "🚫", label: "Repeat Question (already answered)", gp: "0 GP", zero: true },
];

const faqs = [
  {
    q: "What is Tax Genius Challenge?",
    a: "Tax Genius Challenge is India's gamified Tax learning platform. Start with Income Tax (Old Act 1961 vs New Act 2025) and unlock more topics — GST, TDS, Capital Gains, and Corporate Tax — as they launch. Earn GP, win 1v1 challenges, and collect certificates.",
  },
  {
    q: "How do 1v1 Challenges work?",
    a: "Challenge any friend by email. Both of you answer the same 15 questions. Highest score wins +150 GP. Ties broken by speed. Share your victories on WhatsApp and Twitter.",
  },
  {
    q: "What are Genius Points (GP)?",
    a: "GP are earned for correct answers with difficulty weights (Basic 8, Moderate 10, Advanced 15, Expert 20 GP), plus speed and streak bonuses. GP determines your leaderboard position. Expert level unlocks after you've answered every Basic, Moderate, and Advanced question. Important: you earn GP only the first time you answer a question — repeat questions always award 0 GP, so keep exploring new questions!",
  },
  {
    q: "How do I earn Certificates?",
    a: "Certificates are awarded for unique questions answered: Bronze Tax Scholar (100), Silver Tax Analyst (250), Gold Tax Advisor (500), Platinum Tax Strategist (750), Diamond Tax Master (1,000). Download them as PNG to share on LinkedIn!",
  },
  {
    q: "When does the New Income Tax Act 2025 come into effect?",
    a: "The New Income Tax Act 2025 comes into effect from April 1, 2026, replacing the 63-year-old Income Tax Act 1961. Tax Genius quizzes cover every key difference.",
  },
  {
    q: "Is Tax Genius free?",
    a: "Yes — completely free for everyone learning Indian Taxation. Register, start quizzing, and climb the leaderboard at no cost.",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dark, setDark] = useDarkMode();

  const D = dark;

  return (
    <div className={D ? "dark-page" : "light-page"} style={{
      backgroundColor: D ? "#0f172a" : "#f8fafc",
      color: D ? "#f1f5f9" : "#0f172a",
      minHeight: "100vh",
      transition: "background-color 0.3s, color 0.3s",
    }}>

      {/* ── NAV ── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
        backgroundColor: D ? "rgba(15,23,42,0.85)" : "rgba(255,255,255,0.85)",
        backdropFilter: "blur(12px)",
        borderBottom: D ? "1px solid #1e293b" : "1px solid #e2e8f0",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 40, height: 40, background: "linear-gradient(135deg,#10b981,#0d9488)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "#fff", fontWeight: 900, fontSize: 20 }}>T</span>
            </div>
            <span style={{ fontWeight: 800, fontSize: 20, fontFamily: "Nunito,sans-serif", color: D ? "#f1f5f9" : "#0f172a" }}>
              Tax Genius
            </span>
          </div>

          {/* Right controls */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Dark mode toggle */}
            <button
              onClick={() => setDark(!D)}
              style={{
                width: 40, height: 40, borderRadius: "50%", border: "none", cursor: "pointer",
                backgroundColor: D ? "#1e293b" : "#f1f5f9",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s",
              }}
              title={D ? "Switch to light mode" : "Switch to dark mode"}
            >
              {D ? <Sun size={18} color="#fbbf24" /> : <Moon size={18} color="#64748b" />}
            </button>

            {user ? (
              <button
                onClick={() => navigate("/dashboard")}
                style={{ background: "linear-gradient(135deg,#10b981,#0d9488)", color: "#fff", border: "none", borderRadius: 999, padding: "8px 24px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
              >
                Dashboard
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  style={{ background: "transparent", border: "none", color: D ? "#94a3b8" : "#64748b", cursor: "pointer", fontWeight: 600, fontSize: 14 }}
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/register")}
                  style={{ background: "linear-gradient(135deg,#10b981,#0d9488)", color: "#fff", border: "none", borderRadius: 999, padding: "8px 24px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}
                >
                  Get Started Free →
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={{ paddingTop: 140, paddingBottom: 80, paddingLeft: 24, paddingRight: 24, position: "relative", overflow: "hidden" }}>
        {/* Gradient orbs */}
        <div style={{ position: "absolute", top: 60, left: "10%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle,rgba(16,185,129,0.15),transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: 100, right: "5%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle,rgba(245,158,11,0.12),transparent 70%)", pointerEvents: "none" }} />

        {/* Floating emoji */}
        {[
          { emoji: "📜", top: "15%", left: "5%", delay: 0 },
          { emoji: "🏆", top: "20%", right: "8%", delay: 0.4 },
          { emoji: "💡", top: "55%", left: "3%", delay: 0.8 },
          { emoji: "🇮🇳", top: "60%", right: "5%", delay: 0.6 },
        ].map((e, i) => (
          <motion.div key={i}
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 3 + i * 0.3, repeat: Infinity, delay: e.delay }}
            style={{ position: "absolute", fontSize: 36, ...e, pointerEvents: "none" }}
          >
            {e.emoji}
          </motion.div>
        ))}

        <div style={{ maxWidth: 860, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 1 }}>
          {/* Badge */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              background: D ? "#1e3a2f" : "#dcfce7",
              color: D ? "#4ade80" : "#166534",
              borderRadius: 999, padding: "6px 16px", fontSize: 13, fontWeight: 700, marginBottom: 24,
            }}>
              <Flame size={14} /> India's Tax Challenge Platform · More Topics Coming Soon
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            style={{ fontFamily: "Nunito,sans-serif", fontWeight: 900, fontSize: "clamp(36px,6vw,72px)", lineHeight: 1.1, marginBottom: 24 }}
          >
            Master{" "}
            <span style={{ background: "linear-gradient(135deg,#10b981,#0d9488)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              Indian Tax
            </span>
            <br />— the Fun Way
          </motion.h1>

          {/* Sub */}
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            style={{ fontSize: 18, color: D ? "#94a3b8" : "#475569", marginBottom: 36, lineHeight: 1.7, maxWidth: 600, margin: "0 auto 36px" }}
          >
            Quiz-based Tax Challenge platform for CAs, tax professionals &amp; students.
            Start with Income Tax (Old Act 1961 vs New Act 2025), earn certificates,
            and compete on the leaderboard. GST, TDS &amp; more coming soon.
          </motion.p>

          {/* CTA buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 56 }}
          >
            <button
              onClick={() => navigate(user ? "/dashboard" : "/register")}
              style={{
                background: "linear-gradient(135deg,#10b981,#0d9488)", color: "#fff", border: "none",
                borderRadius: 999, padding: "16px 36px", fontWeight: 800, fontSize: 16, cursor: "pointer",
                boxShadow: "0 8px 24px rgba(16,185,129,0.4)", display: "flex", alignItems: "center", gap: 8,
              }}
            >
              Start the Challenge Free <ChevronRight size={18} />
            </button>
            <button
              onClick={() => navigate(user ? "/challenges" : "/register")}
              style={{
                background: D ? "#1e293b" : "#fff", color: D ? "#f1f5f9" : "#0f172a",
                border: D ? "2px solid #334155" : "2px solid #e2e8f0",
                borderRadius: 999, padding: "16px 36px", fontWeight: 700, fontSize: 16, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 8,
              }}
            >
              <Swords size={18} /> Challenge a Friend
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            style={{ display: "flex", justifyContent: "center", gap: 48, flexWrap: "wrap" }}
          >
            {[
              { icon: <Users size={18} color="#10b981" />, label: "Active Learners", val: 1000, suffix: "+" },
              { icon: <BookOpen size={18} color="#f59e0b" />, label: "Questions", val: 1000, suffix: "+" },
              { icon: <Trophy size={18} color="#6366f1" />, label: "Quizzes Taken", val: 5000, suffix: "+" },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", marginBottom: 4 }}>
                  {s.icon}
                  <span style={{ fontWeight: 800, fontSize: 22, fontFamily: "Nunito,sans-serif" }}>
                    <Counter target={s.val} suffix={s.suffix} />
                  </span>
                </div>
                <span style={{ fontSize: 12, color: D ? "#64748b" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>{s.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── OLD vs NEW COMPARISON ── */}
      <section style={{ padding: "80px 24px", backgroundColor: D ? "#0f172a" : "#f8fafc" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 48 }}>
            <h2 style={{ fontFamily: "Nunito,sans-serif", fontWeight: 800, fontSize: "clamp(24px,4vw,40px)", marginBottom: 12 }}>
              Old Act 1961 vs New Act 2025
            </h2>
            <p style={{ color: D ? "#94a3b8" : "#64748b", fontSize: 16 }}>Know exactly what changed — our quizzes cover every key difference</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            style={{ borderRadius: 24, overflow: "hidden", border: D ? "1px solid #1e293b" : "1px solid #e2e8f0", boxShadow: D ? "0 4px 40px rgba(0,0,0,0.4)" : "0 4px 40px rgba(0,0,0,0.06)" }}
          >
            {/* Header */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", backgroundColor: D ? "#1e293b" : "#f1f5f9" }}>
              <div style={{ padding: "16px 20px", fontWeight: 700, fontSize: 13, color: D ? "#94a3b8" : "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>Topic</div>
              <div style={{ padding: "16px 20px", fontWeight: 700, fontSize: 13, color: "#ef4444", textTransform: "uppercase", letterSpacing: "0.08em", borderLeft: D ? "1px solid #334155" : "1px solid #e2e8f0" }}>
                Old Act 1961
              </div>
              <div style={{ padding: "16px 20px", fontWeight: 700, fontSize: 13, color: "#10b981", textTransform: "uppercase", letterSpacing: "0.08em", borderLeft: D ? "1px solid #334155" : "1px solid #e2e8f0" }}>
                New Act 2025
              </div>
            </div>
            {/* Rows */}
            {comparisons.map((row, i) => (
              <div key={i} style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                backgroundColor: i % 2 === 0 ? (D ? "#0f172a" : "#fff") : (D ? "#0b1120" : "#f8fafc"),
                borderTop: D ? "1px solid #1e293b" : "1px solid #f1f5f9",
              }}>
                <div style={{ padding: "14px 20px", fontWeight: 600, fontSize: 14, color: D ? "#cbd5e1" : "#334155" }}>{row.topic}</div>
                <div style={{ padding: "14px 20px", fontSize: 14, color: "#ef4444", borderLeft: D ? "1px solid #1e293b" : "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 6 }}>
                  <XCircle size={14} /> {row.old}
                </div>
                <div style={{ padding: "14px 20px", fontSize: 14, color: "#10b981", borderLeft: D ? "1px solid #1e293b" : "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 6 }}>
                  <CheckCircle size={14} /> {row.new_}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── TOPICS ROADMAP ── */}
      <section style={{ padding: "80px 24px", backgroundColor: D ? "#0b1120" : "#ffffff" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 48 }}>
            <h2 style={{ fontFamily: "Nunito,sans-serif", fontWeight: 800, fontSize: "clamp(24px,4vw,40px)", marginBottom: 12 }}>Tax Challenge Topics</h2>
            <p style={{ color: D ? "#94a3b8" : "#64748b", fontSize: 16 }}>Start with Income Tax — more topics launching soon</p>
          </motion.div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16 }}>
            {[
              { emoji: "📘", label: "Income Tax", desc: "Old Act 1961 vs New Act 2025", badge: "1,000 Questions", active: true },
              { emoji: "🧾", label: "GST", desc: "Goods & Services Tax", badge: "Coming Soon", active: false },
              { emoji: "💼", label: "TDS & TCS", desc: "Deduction at Source", badge: "Coming Soon", active: false },
              { emoji: "📈", label: "Capital Gains", desc: "LTCG · STCG · Indexation", badge: "Coming Soon", active: false },
              { emoji: "🏢", label: "Corporate Tax", desc: "MAT · Section 115BA", badge: "Coming Soon", active: false },
            ].map((t, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                style={{
                  borderRadius: 20, padding: "24px 20px", textAlign: "center",
                  background: t.active
                    ? (D ? "linear-gradient(135deg,#0d2a1e,#1a3a2a)" : "linear-gradient(135deg,#f0fdf4,#dcfce7)")
                    : (D ? "#0f172a" : "#f8fafc"),
                  border: t.active
                    ? "2px solid #10b981"
                    : (D ? "1px dashed #334155" : "1px dashed #cbd5e1"),
                  opacity: t.active ? 1 : 0.7,
                  cursor: t.active ? "pointer" : "default",
                }}
              >
                <span style={{ fontSize: 36, display: "block", marginBottom: 10 }}>{t.emoji}</span>
                <p style={{ fontWeight: 800, fontSize: 14, color: t.active ? "#10b981" : (D ? "#64748b" : "#94a3b8"), marginBottom: 4 }}>{t.label}</p>
                <p style={{ fontSize: 12, color: D ? "#475569" : "#94a3b8", marginBottom: 10, lineHeight: 1.4 }}>{t.desc}</p>
                <span style={{
                  fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "3px 10px",
                  background: t.active ? "#10b981" : (D ? "#1e293b" : "#f1f5f9"),
                  color: t.active ? "#fff" : (D ? "#64748b" : "#94a3b8"),
                }}>{t.badge}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES GRID ── */}
      <section style={{ padding: "80px 24px", backgroundColor: D ? "#0f172a" : "#f8fafc" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 56 }}>
            <h2 style={{ fontFamily: "Nunito,sans-serif", fontWeight: 800, fontSize: "clamp(24px,4vw,40px)", marginBottom: 12 }}>Everything You Need to Conquer Indian Tax</h2>
            <p style={{ color: D ? "#94a3b8" : "#64748b", fontSize: 16 }}>Gamified, AI-powered, and built for CAs, students &amp; tax professionals</p>
          </motion.div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 24 }}>
            {[
              { icon: <Brain size={28} color="#10b981" />, title: "AI-Powered Questions", desc: "1,000+ questions on Indian Tax topics, generated and curated by AI", bg: D ? "#0d2a1e" : "#f0fdf4" },
              { icon: <Swords size={28} color="#f43f5e" />, title: "1v1 Challenges", desc: "Battle friends in real-time tax quizzes. Winner takes +150 Genius Points", bg: D ? "#2a0d1a" : "#fff1f2" },
              { icon: <Trophy size={28} color="#f59e0b" />, title: "Leaderboard", desc: "Compete nationally. Weekly and all-time rankings updated in real-time", bg: D ? "#2a1d0d" : "#fffbeb" },
              { icon: <Award size={28} color="#6366f1" />, title: "Downloadable Certificates", desc: "Earn Bronze → Diamond certificates. Download as PNG for LinkedIn", bg: D ? "#12102a" : "#f5f3ff" },
              { icon: <Zap size={28} color="#0ea5e9" />, title: "Lifelines", desc: "50:50, Skip, and Extra Time lifelines to help during tough questions", bg: D ? "#0a1e2a" : "#f0f9ff" },
              { icon: <TrendingUp size={28} color="#8b5cf6" />, title: "Progress Tracking", desc: "Track accuracy by difficulty, weak topics, streaks and quiz history", bg: D ? "#1a1028" : "#faf5ff" },
            ].map((f, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                whileHover={{ y: -4, boxShadow: D ? "0 12px 32px rgba(0,0,0,0.4)" : "0 12px 32px rgba(0,0,0,0.08)" }}
                style={{
                  backgroundColor: f.bg, borderRadius: 20, padding: 28,
                  border: D ? "1px solid #1e293b" : "1px solid #f1f5f9",
                  transition: "all 0.2s",
                }}
              >
                <div style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: D ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                  {f.icon}
                </div>
                <h3 style={{ fontFamily: "Nunito,sans-serif", fontWeight: 800, fontSize: 16, marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: D ? "#94a3b8" : "#64748b", lineHeight: 1.6 }}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── GP SCORING SYSTEM ── */}
      <section style={{ padding: "80px 24px", backgroundColor: D ? "#0f172a" : "#f8fafc" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 48 }}>
            <h2 style={{ fontFamily: "Nunito,sans-serif", fontWeight: 800, fontSize: "clamp(24px,4vw,40px)", marginBottom: 12 }}>How Genius Points Work</h2>
            <p style={{ color: D ? "#94a3b8" : "#64748b", fontSize: 16 }}>Earn more GP by being fast, accurate, and consistent</p>
          </motion.div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 16 }}>
            {scoring.map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  backgroundColor: s.zero ? (D ? "#1a0a0a" : "#fff5f5") : s.highlight ? (D ? "#1e1040" : "#f5f3ff") : (D ? "#1e293b" : "#fff"),
                  borderRadius: 14, padding: "14px 20px",
                  border: s.zero ? `1px solid ${D ? "#7f1d1d" : "#fecaca"}` : s.highlight ? `1px solid ${D ? "#6d28d9" : "#c4b5fd"}` : (D ? "1px solid #334155" : "1px solid #e2e8f0"),
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 22 }}>{s.icon}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: s.zero ? (D ? "#fca5a5" : "#dc2626") : s.highlight ? (D ? "#c4b5fd" : "#6d28d9") : (D ? "#cbd5e1" : "#334155") }}>{s.label}</span>
                </div>
                <span style={{
                  fontWeight: 800, fontSize: 15, padding: "4px 12px", borderRadius: 999,
                  color: s.zero ? "#dc2626" : s.highlight ? "#7c3aed" : "#10b981",
                  backgroundColor: s.zero ? (D ? "#2d0a0a" : "#fee2e2") : s.highlight ? (D ? "#2d1a5e" : "#ede9fe") : (D ? "#0d2a1e" : "#f0fdf4"),
                }}>{s.gp}</span>
              </motion.div>
            ))}
          </div>
          <p style={{ textAlign: "center", color: D ? "#94a3b8" : "#64748b", fontSize: 13, marginTop: 18 }}>
            🚫 Questions you have already answered earn <strong>0 GP</strong> — keep exploring new questions to keep earning!
          </p>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={{ padding: "80px 24px", backgroundColor: D ? "#0b1120" : "#fff" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 56 }}>
            <h2 style={{ fontFamily: "Nunito,sans-serif", fontWeight: 800, fontSize: "clamp(24px,4vw,40px)", marginBottom: 12 }}>How It Works</h2>
            <p style={{ color: D ? "#94a3b8" : "#64748b", fontSize: 16 }}>Get started in under 60 seconds</p>
          </motion.div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 32 }}>
            {[
              { step: "1", icon: "📝", title: "Register Free", desc: "Create your account in seconds — no credit card needed" },
              { step: "2", icon: "🎯", title: "Take the Quiz", desc: "Answer 15 questions on your chosen Indian Tax topic" },
              { step: "3", icon: "⚡", title: "Earn GP & Badges", desc: "Score points, unlock certificates, and climb the leaderboard" },
              { step: "4", icon: "⚔️", title: "Challenge Friends", desc: "Invite colleagues to 1v1 battles and prove you're the Tax Genius" },
            ].map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
                style={{ textAlign: "center" }}
              >
                <div style={{
                  width: 64, height: 64, borderRadius: "50%", margin: "0 auto 16px",
                  background: "linear-gradient(135deg,#10b981,#0d9488)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 24, boxShadow: "0 8px 20px rgba(16,185,129,0.3)",
                }}>
                  {s.icon}
                </div>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#10b981", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Step {s.step}</div>
                <h3 style={{ fontFamily: "Nunito,sans-serif", fontWeight: 800, fontSize: 17, marginBottom: 8 }}>{s.title}</h3>
                <p style={{ fontSize: 14, color: D ? "#94a3b8" : "#64748b", lineHeight: 1.6 }}>{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CHALLENGE BANNER ── */}
      <section style={{ padding: "80px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            style={{
              background: "linear-gradient(135deg,#f43f5e,#e11d48)",
              borderRadius: 28, padding: "56px 48px",
              display: "flex", flexWrap: "wrap", gap: 40, alignItems: "center", justifyContent: "space-between",
              boxShadow: "0 20px 60px rgba(244,63,94,0.3)",
            }}
          >
            <div style={{ flex: "1 1 300px" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 999, padding: "6px 14px", marginBottom: 20 }}>
                <Swords size={14} color="#fff" />
                <span style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>1v1 Challenge Mode</span>
              </div>
              <h2 style={{ fontFamily: "Nunito,sans-serif", fontWeight: 900, fontSize: "clamp(24px,4vw,40px)", color: "#fff", marginBottom: 16, lineHeight: 1.2 }}>
                Who's the Tax Genius<br />in Your Office?
              </h2>
              <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 16, marginBottom: 28, lineHeight: 1.6 }}>
                Challenge your CA colleagues to a head-to-head battle. Same 15 questions, same time — highest score wins 150 GP and bragging rights.
              </p>
              <button
                onClick={() => navigate(user ? "/challenges" : "/register")}
                style={{ backgroundColor: "#fff", color: "#e11d48", border: "none", borderRadius: 999, padding: "14px 32px", fontWeight: 800, fontSize: 15, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
              >
                <Swords size={16} /> Start a Challenge
              </button>
            </div>
            <div style={{ flex: "1 1 280px", backgroundColor: "rgba(255,255,255,0.12)", borderRadius: 20, padding: 28 }}>
              {[
                { emoji: "1", text: "Send challenge to friend's email" },
                { emoji: "2", text: "Both answer same 15 questions" },
                { emoji: "🏆", text: "Highest score wins +150 GP" },
                { emoji: "📱", text: "Share victory on WhatsApp" },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: i < 3 ? 20 : 0 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: "#fff", fontWeight: 800, flexShrink: 0 }}>
                    {r.emoji}
                  </div>
                  <span style={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>{r.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CERTIFICATES ── */}
      <section style={{ padding: "80px 24px", backgroundColor: D ? "#0b1120" : "#fff" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 style={{ fontFamily: "Nunito,sans-serif", fontWeight: 800, fontSize: "clamp(24px,4vw,40px)", marginBottom: 12 }}>Earn & Download Certificates</h2>
            <p style={{ color: D ? "#94a3b8" : "#64748b", fontSize: 16, marginBottom: 48 }}>Share your Tax Genius credentials on LinkedIn and WhatsApp</p>
            <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
              {[
                { emoji: "🥉", name: "Bronze Tax Scholar",      level: "Bronze",   q: "100 Questions",   color: "#ea580c", bg: D ? "#2a1208" : "#fff7ed" },
                { emoji: "🥈", name: "Silver Tax Analyst",      level: "Silver",   q: "250 Questions",   color: "#64748b", bg: D ? "#1e293b" : "#f8fafc" },
                { emoji: "🥇", name: "Gold Tax Advisor",        level: "Gold",     q: "500 Questions",   color: "#d97706", bg: D ? "#292007" : "#fffbeb" },
                { emoji: "💎", name: "Platinum Tax Strategist", level: "Platinum", q: "750 Questions",   color: "#4f46e5", bg: D ? "#12102a" : "#f5f3ff" },
                { emoji: "👑", name: "Diamond Tax Master",      level: "Diamond",  q: "1,000 Questions", color: "#0891b2", bg: D ? "#082028" : "#ecfeff" },
              ].map((c, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -6 }}
                  style={{
                    backgroundColor: c.bg, borderRadius: 18, padding: "24px 20px", textAlign: "center", width: 140,
                    border: `2px solid ${c.color}30`, cursor: "default",
                  }}
                >
                  <span style={{ fontSize: 36, display: "block", marginBottom: 8 }}>{c.emoji}</span>
                  <p style={{ fontWeight: 800, fontSize: 13, color: c.color, marginBottom: 4 }}>{c.name}</p>
                  <p style={{ fontSize: 11, color: D ? "#94a3b8" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{c.level}</p>
                  <p style={{ fontSize: 11, color: D ? "#64748b" : "#94a3b8" }}>{c.q}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: "80px 24px", backgroundColor: D ? "#0f172a" : "#f8fafc" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 48 }}>
            <h2 style={{ fontFamily: "Nunito,sans-serif", fontWeight: 800, fontSize: "clamp(24px,4vw,40px)", marginBottom: 12 }}>Frequently Asked Questions</h2>
          </motion.div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {faqs.map((f, i) => (
              <FAQItem key={i} q={f.q} a={f.a} dark={D} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ padding: "80px 24px", background: "linear-gradient(135deg,#10b981,#0d9488)" }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
          <span style={{ fontSize: 48, display: "block", marginBottom: 16 }}>🎯</span>
          <h2 style={{ fontFamily: "Nunito,sans-serif", fontWeight: 900, fontSize: "clamp(28px,5vw,48px)", color: "#fff", marginBottom: 16, lineHeight: 1.2 }}>
            Ready to Become a Tax Genius?
          </h2>
          <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 17, marginBottom: 36 }}>
            Join India's fastest-growing tax learning community. Free forever.
          </p>
          <button
            onClick={() => navigate(user ? "/dashboard" : "/register")}
            style={{
              backgroundColor: "#fff", color: "#059669", border: "none",
              borderRadius: 999, padding: "18px 48px", fontWeight: 900, fontSize: 17, cursor: "pointer",
              boxShadow: "0 8px 30px rgba(0,0,0,0.2)", display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >
            Start for Free <ChevronRight size={20} />
          </button>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, marginTop: 16 }}>No credit card · No download · Instant access</p>
        </motion.div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ backgroundColor: D ? "#020617" : "#0f172a", padding: "40px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, background: "linear-gradient(135deg,#10b981,#0d9488)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#fff", fontWeight: 900, fontSize: 16 }}>T</span>
              </div>
              <span style={{ fontWeight: 800, fontSize: 16, color: "#f1f5f9", fontFamily: "Nunito,sans-serif" }}>Tax Genius Challenge</span>
            </div>
            <p style={{ color: "#475569", fontSize: 13 }}>A project by AI-for-India &copy; 2026 TaxGenius.online</p>
            <div style={{ display: "flex", gap: 20 }}>
              {["Dashboard", "Leaderboard", "Login"].map((l, i) => (
                <button key={i} onClick={() => navigate(`/${l.toLowerCase()}`)} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ── FAQ accordion item ────────────────────────────────────────────────────────
function FAQItem({ q, a, dark: D }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      backgroundColor: D ? "#1e293b" : "#fff", borderRadius: 16,
      border: D ? "1px solid #334155" : "1px solid #e2e8f0",
      overflow: "hidden",
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "18px 24px", background: "none", border: "none", cursor: "pointer",
          textAlign: "left", fontWeight: 700, fontSize: 15, color: D ? "#f1f5f9" : "#0f172a",
          gap: 12,
        }}
      >
        {q}
        <span style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", backgroundColor: D ? "#334155" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, transition: "transform 0.2s", transform: open ? "rotate(45deg)" : "rotate(0deg)" }}>+</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: "hidden" }}
          >
            <p style={{ padding: "0 24px 18px", fontSize: 14, color: D ? "#94a3b8" : "#64748b", lineHeight: 1.7 }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
